<template>
	<div>

		<div v-if="isUpdate && activeFlags.length > 0" class="alerts">
			<ul v-for="flag in activeFlags" :key="`flag-top-${flag.id}`" :class="`${flag.color}-bg`">
				<li><span v-if="flag.icon" class="fa" :class="flag.icon"></span><span v-html="flag.content"></span></li>
			</ul>
		</div>

		<div v-if="!hasChildModules || !isUpdate" class="title-container">
			<div class="title">{{ isUpdate ? 'Update' : 'Add' }} {{ module.singular_name }}</div>
			<slot name="buttons"></slot>
			<module-page-buttons-component v-if="!$slots.hasOwnProperty('buttons')"></module-page-buttons-component>
		</div>

		<div v-if="hasChildModules && isUpdate" class="title-container">
			<div class="tabbed-buttons">
				<a href @click.prevent="activeTab = null" class="tab" :class="{ active: activeTab === null }"><span class="fa" :class="module.icon"></span>{{ module.singular_name }}</a>
				<a href v-for="childModule in childModules" :key="`child-module-tab-${ childModule.id }`" @click.prevent="activeTab = childModule.slug" :class="{ active: activeTab === childModule.slug }"><span class="fa" :class="childModule.icon"></span>{{ childModule.name }}<span class="counter">{{ (row[ childModule.plural_table_name ] || []).length }}</span></a>
			</div>
			<slot name="buttons"></slot>
			<module-page-buttons-component v-if="!$slots.hasOwnProperty('buttons')" :parent-row="row"></module-page-buttons-component>
		</div>

		<form v-show="activeTab === null" ref="form" method="post" @submit.prevent="onSubmit" enctype="multipart/form-data">
			<component
				v-for="field in visibleFields" :key="`v_${field.id}`"
				:ref="field.slug"
				v-model="row"
				:form-id="formId"
				:field="field"
				:module="moduleData.module"
				:tags="tags['v_' + field.id] || []"
				:options="associatedModules['v_' + field.id] || []"
				:is="getFieldComponent(field)"
				:show-errors="hasSubmittedForm"
			/>

			<div v-if="hasChildModules && !isUpdate" class="input-container">
				<div class="input-static-text">
					After you save, you will be able to manage the {{ childModules[0].name.toLowerCase() }} for this {{ module.singular_name.toLowerCase() }}.
				</div>
			</div>

			<div v-if="saving" class="form-progress-container">
				<div class="form-progress-bar" :style="`width: ${uploadProgress}%;`"></div>
			</div>

			<div v-if="isSaveBlocked">
				<div v-if="activeFlags.length > 0" class="alerts">
					<ul v-for="flag in activeFlags" :key="`flag-top-${flag.id}`" :class="`${flag.color}-bg`">
						<li><span v-if="flag.icon" class="fa" :class="flag.icon"></span><span v-html="flag.content"></span></li>
					</ul>
				</div>
			</div>

			<submit-button v-if="!isSaveBlocked" :text="saveButtonText" error-text="Failed: See Problems Above" :has-errors="hasSubmittedForm && !saving && !done" :icon="isUpdate ? 'fa-save' : 'fa-save'" :loading="saving"></submit-button>
		</form>

		<div v-if="hasChildModules && isUpdate" v-show="activeTab !== null">
			<div v-show="activeTab === childModule.slug" v-for="childModule in childModules" :key="childModule.id">

				<div class="title-container">
					<div class="title">{{ (row[ childModule.plural_table_name ] || []).length }} {{ heading(childModule, row[ childModule.plural_table_name ]) }}</div>
					<div class="buttons">
						<a href class="button" @click.prevent="() => openCreateModal(childModule)"><div class="hover-title">Add {{ childModule.singular_name }}</div><span class="fa fa-plus"></span></a>
					</div>
				</div>

				<list-component
					:rows="row[ childModule.plural_table_name ] || []"
					:searchable="childModule.searchable"
					:module="childModule"
					:loading-state="{
						state: 'LOADED',
						loadedOptions: {
							page: 1,
							filters: [],
						},
					}"
					:has-gallery="moduleHasGallery(childModule)"
					:has-file-gallery="moduleHasFileGallery(childModule)"
					:modal="true"
					@removed="(deletedRow) => removeRow(childModule, deletedRow)"
				></list-component>
			</div>
		</div>
	</div>
</template>

<script>

import _ from 'underscore';
import { mapState } from 'vuex';

import ListComponent from './ListComponent';
import CrudForm from '@/mixins/CrudForm';
// import RowTemplateBuilder from '@/utils/RowTemplateBuilder';
import ModulePageButtonsComponent from './ModulePageButtonsComponent';
import { DRAWERS } from '@/constants';

export default {

	name: 'ModuleForm',

	mixins: [CrudForm],

	props: {
		row: {
			type: Object,
			required: true,
		},
		isDrawer: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			module: this.moduleData.module,

			// The options available on associated module field types - keyed by field id: v_[FIELD_ID]
			associatedModules: this.moduleData.associatedModules || {},
			tags: this.moduleData.tags || {},

			originalRow: { ...this.row },

			// Whether we are actively saving the record
			saving: false,

			// Whether the saving is completed successfully
			done: false,

			// The upload progress to the server on submit
			uploadProgress: 0,

			// Form validation errors
			errors: [],

			hasSubmittedForm: false,

			// An identifier to ensure emitted events only get picked up by this form
			formId: this.row.hasOwnProperty('id') ? this.row.id : _.uniqueId(),

			// If this is an update page with child modules, enable tabbing
			activeTab: !this.isDrawer ? (this.$route.params.page || null) : null,

			// Have to store active flags so they don't get applied from editing the row (before saving)
			activeFlags: [],
		};
	},

	computed: {

		...mapState({
			modules: state => state.modules,
		}),

		isUpdate() {
			return (this.row && this.row.id);
		},

		isSaveBlocked() {
			return this.activeFlags.filter(flag => !!flag.prevent_save).length > 0;
		},

		formURL() {
			if (this.isUpdate) {
				return `/module/${this.module.slug}/${this.row.id}/update`;
			}

			return `/module/${this.module.slug}/store`;
		},

		saveStatus() {
			if (this.uploadProgress === 100 && !this.saving) {
				return 'Saved';
			}

			return this.saving ? 'Saving' : 'Not Saved';
		},

		saveButtonText() {
			let text = (this.isUpdate ? 'Update ' : 'Add ') + this.module.singular_name;
			if (this.hasChildModules) {
				text = 'Save ' + this.module.singular_name + ' Details';
			}

			return text;
		},

		isProcessing() {
			return this.saving && this.uploadProgress < 100;
		},

		activeChildModule() {
			if (this.hasChildModules && this.activeTab !== null) {
				return this.childModules.find(module => module.slug === this.activeTab);
			}

			return null;
		},

	},

	watch: {

		row: {
			deep: true,
			handler: function() {
				this.$forceUpdate();
			}
		},

		activeTab: function(newActiveTab) {
			if (!this.isDrawer) {
				this.$router.replace({ name: 'edit', params: { ...this.$route.params, page: newActiveTab } });
			}
		},

	},

	mounted() {
		this.$root.$on('modal-form-saved', ({ module, response }) => {
			if (typeof this.row[ module.plural_table_name ] !== 'undefined') {
				this.addOrUpdateRow(module, response.data);
			}
		});

		if (this.activeChildModule && this.$route.params.childId) {
			const row = this.row[ this.activeChildModule.plural_table_name ].find(childRow => childRow.id == this.$route.params.childId);
			if (row) {
				this.$root.$emit('open-drawer', DRAWERS.MODULE_FORM, {
					module: this.activeChildModule,
					id: row.id,
					formData: row,
					isUpdate: true,
				});
			}
		}

		this.activeFlags = this.module.flags.filter(flag => this.passesFlagConditions(flag));
	},

	methods: {

		/**
		 * Open a Create form in a modal for the given child module
		 */
		openCreateModal(childModule) {
			let formData = {};

			if (this.row.id) {
				formData[ `foreign_keys[${this.module.singular_table_name}_id]` ] = this.row.id;
			}

			this.$root.$emit('open-drawer', DRAWERS.MODULE_FORM, { module: childModule, formData, id: this.row.id });
			// this.$modal.show('modal-form', {
			// 	module: this.modules[ childModule.plural_table_name ],
			// 	formData,
			// });
		},

		heading(module, rows) {
			let name = module.singular_name;
			if ((rows || []).length != 1) {
				const lastLetter = name.slice(-1);
				switch (lastLetter) {
					case 'y':
						return name.slice(0, -1) + 'ies';
					case 's':
						return name;
					default:
						return name + 's';
				}
			}

			return name;
		},

		addOrUpdateRow(module, row) {
			const existingIndex = _.findIndex(this.row[ module.plural_table_name ], { id: row.id });
			if (existingIndex !== -1) {
				this.$set(this.row[ module.plural_table_name ], existingIndex, row);
			} else {
				// Add
				this.row[ module.plural_table_name ].unshift(row);
			}
		},

		removeRow(module, row) {
			const rows = this.row[ module.plural_table_name ];
			const existingIndex = _.findIndex(rows, { id: row.id });
			if (existingIndex !== -1) {
				rows.splice(existingIndex, 1);
			}
		},

		getFormData() {
			const formData = new FormData();
			if (typeof formData.entries !== 'function') {
				return formData;
			}

			if (typeof this.row === 'object') {
				Object.keys(this.row).forEach(key => {
					formData.append(key, this.row[ key ]);
				});
			}

			this.visibleFields.forEach(field => {
				// const key = field.type === 'associated_module' ? field.specific_data : field.slug;
				const key = field.slug;

				if (this.fileUploadFieldTypes.indexOf(field.type) !== -1) {
					// File upload
					// this.$refs.form.querySelector()
				} else if (this.row.hasOwnProperty(key)) {
					// Regular Field
					let value = this.row[ key ];
					if (_.isArray(value)) {
						value.forEach(entry => formData.append(key + '[]', entry));
					} else {
						if (value === null || value === 'null') {
							value = '';
						} else if (typeof value === 'boolean') {
							value = value ? 1 : 0;
						}
						formData.append(key, value);
					}
				}
			});

			this.$refs.form.querySelectorAll('input[type="file"]').forEach(fileField => {
				if (fileField.files.length > 0) {
					formData.append(fileField.name, fileField.files[ 0 ]);
				}
			});

			return formData;
		},

		getSelectedTags(field) {
			if (field.type === 'associated_module') {
				// return this.selectedOptions[ 'v_' + field.id ] || [];
				return this.row[ field.slug ];
			}

			return (this.row.tags || []).filter(tag => tag.module_field_id === field.id);
		},


		onSubmit(e) {
			e.preventDefault();
			if (this.isSaveBlocked) return;

			this.done = false;
			this.hasSubmittedForm = true;
			this.$root.$emit(this.formId + '_validate-fields');

			this.getFormData();

			if (this.formIsValid()) {
				this.saving = true;

				const config = { onUploadProgress: this.onSaveUploadProgress.bind(this) };

				try {
					this.$api.post(this.formURL, this.getFormData(), config)
						.then(this.onSaveSuccess.bind(this)).catch(this.onSaveFailure.bind(this));
				} catch(e) {}
			}
		},

		/**
		 * Update progress
		 *
		 * @param progressEvent
		 */
		onSaveUploadProgress(progressEvent) {
			this.uploadProgress = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
		},

		onSaveSuccess(response) {
			this.$emit('input', {
				...this.row,
				...response.data,
			});
			this.saving = false;
			this.done = true;
			this.uploadProgress = 100;
			this.$store.dispatch('addToast', `${this.module.singular_name} Has Been Saved`);
			this.$emit('saved', response);
		},

		onSaveFailure(e) {
			this.$store.dispatch('addToast', { message: this.$api.getErrorMessage(e), type: 'bad-bg' }, { root: true });
			this.saving = false;
		},

		moduleHasGallery(module) {
			return module.fields.filter(field => field.type === 'photo_gallery').length > 0;
		},

		moduleHasFileGallery(module) {
			return module.fields.filter(field => field.type === 'file_gallery').length > 0;
		},

		passesFlagConditions(flag) {
			if (!this.isUpdate) return false;
			const conditions = (flag.conditions || []);
			const matchedConditions = conditions.filter(condition => {
				const conditionField = _.findWhere(this.module.fields, { slug: condition.field });
				if (conditionField) {
					switch (condition.operand) {
						case '=':
							return this.row[ conditionField.slug ] == condition.value;
						case '!=':
							return this.row[ conditionField.slug ] != condition.value;
						case 'empty':
							return _.isEmpty(this.row[ conditionField.slug ]);
						case 'not empty':
							return !_.isEmpty(this.row[ conditionField.slug ]);
						default:
							return true;
					}
				}

				return false;
			});

			switch (flag.condition_grouping) {
				case 'all':
					return matchedConditions.length === conditions.length;
				case 'any':
				default:
					return matchedConditions.length > 0;
			}
		},

	},

	components: {
		ModulePageButtonsComponent,
		ListComponent,
	},
};
</script>
